import { useCallback, useRef } from 'react';
import {
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult
} from 'react-query';

import createFetchData from '../utils/createFetchData';
import generateQueryKey from '../utils/generateQueryKey';
import { CustomFetchResponse } from '../constants/types';

export interface UseResourceQueryOptions<TResource = unknown>
  extends Omit<
    UseQueryOptions<CustomFetchResponse<TResource>>,
    'queryKey' | 'queryFn'
  > {
  url: string;
  params?: Record<string, any>;
  prefix?: string | string[];
  defaultValue?: any;
  method?: string;
  createFetchDataConfig?: Record<string, any>;
}

export type UseResourceQueryResult<TResource = unknown> = UseQueryResult<
  CustomFetchResponse<TResource>
> & {
  value: TResource;
  queryKey: QueryKey;
  fetch: (...args: any[]) => any;
};

export default function useResourceQuery<TResource = any>({
  params = {},
  prefix,
  url,
  createFetchDataConfig = {},
  defaultValue = {
    meta: {},
    data: []
  } as any,
  method = 'GET',
  ...config
}: UseResourceQueryOptions<TResource>): UseResourceQueryResult<TResource> {
  // add a version tag to the url params to bust any browser caching
  // whenever a new version of the app is deployed
  const updatedParams: Record<string, any> = params ?? {};
  if (!CONFIG.isTest) {
    updatedParams.__version = CONFIG.appVersion;
  }
  const queryKey = generateQueryKey({ params: updatedParams, prefix, url });

  const query = useQuery(
    queryKey,
    () =>
      createFetchData({
        url,
        params: updatedParams,
        method,
        ...createFetchDataConfig
      } as any),
    (config || {}) as any
  );

  const { data, refetch } = query;

  const hasWarnedAboutFetch = useRef(false);

  const fetch = useCallback(
    (...args: any[]) => {
      // HACK: alias fetch --> refetch since fetch was removed in v3
      if (CONFIG.isDevelopment && !hasWarnedAboutFetch.current) {
        window.console.warn(
          `fetch invoked directly on ${url} - use refetch instead`,
          prefix,
          updatedParams
        );
        hasWarnedAboutFetch.current = true;
      }

      return refetch(...(args as []));
    },
    [refetch]
  );

  return {
    ...query,
    value: data ? data.data : defaultValue,
    queryKey,
    fetch
  } as UseResourceQueryResult<TResource>;
}
